/**
 * Bonded Matter: when a product card image fails in the browser (broken URL, zero
 * intrinsic size, etc.) but Liquid still rendered an <img>, replace that tile with
 * the same Sleek `product-1` placeholder markup as `layout/theme.liquid` template
 * `#bm-product-card-placeholder`.
 *
 * Also clears stuck `.media-wrapper.loading` when the image has actually decoded,
 * because Sleek keeps `opacity: 0` on the `<img>` until `loaded` is toggled.
 */
(function () {
  const TEMPLATE_ID = 'bm-product-card-placeholder';

  function getPlaceholderNodes() {
    const tpl = document.getElementById(TEMPLATE_ID);
    if (!tpl || !tpl.content) return [];
    return Array.from(tpl.content.childNodes);
  }

  /**
   * @param {HTMLImageElement} img
   */
  function replaceProductCardImage(img) {
    const cardRoot = img.closest('.product-card__image-wrapper');
    if (!cardRoot) return;

    const mediaWrap =
      img.closest('.media-wrapper.product-card__image--main') ||
      img.closest('.media-wrapper.product-card__image') ||
      img.closest('.media-wrapper');
    if (!mediaWrap || mediaWrap.dataset.bmImageFallbackApplied === 'true') return;

    const nodes = getPlaceholderNodes();
    if (!nodes.length) return;

    mediaWrap.dataset.bmImageFallbackApplied = 'true';
    const aspect = mediaWrap.style.getPropertyValue('--aspect-ratio').trim();
    mediaWrap.replaceChildren();
    if (aspect) mediaWrap.style.setProperty('--aspect-ratio', aspect);
    nodes.forEach(function (n) {
      mediaWrap.appendChild(n.cloneNode(true));
    });
    mediaWrap.classList.remove('loading');
    mediaWrap.classList.add('loaded');

    const motion = cardRoot.querySelector(':scope > a motion-element');
    if (motion) {
      motion.style.opacity = '1';
      motion.style.transform = 'none';
    }
  }

  /**
   * @param {HTMLImageElement} img
   * @returns {boolean}
   */
  function shouldReplaceLoadedImg(img) {
    if (!(img instanceof HTMLImageElement)) return false;
    if (!img.closest('.product-card__image-wrapper')) return false;
    if (img.dataset.bmFallbackChecked === 'true') return false;
    return img.complete && (img.naturalWidth === 0 || img.naturalHeight === 0);
  }

  /**
   * @param {ParentNode} root
   */
  function scanImages(root) {
    root.querySelectorAll('.product-card__image-wrapper img').forEach(function (img) {
      if (!shouldReplaceLoadedImg(img)) return;
      img.dataset.bmFallbackChecked = 'true';
      replaceProductCardImage(img);
    });
  }

  /**
   * Sleek `.media-wrapper.loading > img { opacity: 0 }` — unblock when decode succeeded.
   * @param {ParentNode} root
   */
  function clearStuckLazyMediaWrappers(root) {
    root.querySelectorAll('.product-card__image-wrapper .media-wrapper.loading').forEach(function (wrap) {
      const img = wrap.querySelector('img');
      if (!(img instanceof HTMLImageElement)) return;
      if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
        wrap.classList.remove('loading');
        wrap.classList.add('loaded');
      }
    });
  }

  document.addEventListener(
    'error',
    function (e) {
      const t = e.target;
      if (!(t instanceof HTMLImageElement)) return;
      if (!t.closest('.product-card__image-wrapper')) return;
      if (t.dataset.bmFallbackChecked === 'true') return;
      t.dataset.bmFallbackChecked = 'true';
      replaceProductCardImage(t);
    },
    true
  );

  document.addEventListener(
    'load',
    function (e) {
      const t = e.target;
      if (!(t instanceof HTMLImageElement)) return;
      if (!t.closest('.product-card__image-wrapper')) return;
      const wrap = t.closest('.media-wrapper');
      if (wrap && wrap.classList.contains('loading') && t.naturalWidth > 0 && t.naturalHeight > 0) {
        wrap.classList.remove('loading');
        wrap.classList.add('loaded');
      }
    },
    true
  );

  function runScans() {
    scanImages(document);
    clearStuckLazyMediaWrappers(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runScans);
  } else {
    runScans();
  }

  window.addEventListener('load', function () {
    runScans();
    window.setTimeout(runScans, 1000);
    window.setTimeout(runScans, 2800);
  });

  document.addEventListener('shopify:section:load', function () {
    window.setTimeout(runScans, 50);
  });
})();
